import { UseTeamListQueryOptions, useTeamListQuery } from './useTeamListQuery';

export interface MyTeamRoleStatus {
  /** `OWNER` while `team.list` is loading or failed, same fallback `useMyTeamRole` always used. */
  role: 'OWNER' | 'MEMBER';
  /** `false` only while `team.list` is still loading. An error still counts as resolved (there is
   *  nothing further to wait for), so a caller that only needs "is the answer final yet" does not
   *  wait forever on a failed request. */
  isResolved: boolean;
}

/**
 * The caller's team role and whether `team.list` has actually answered yet.
 *
 * Most readers only need `useMyTeamRole` below: a loading or failed read defaults to `OWNER`, so a
 * checkout or upgrade button is never wrongly denied while the request is in flight (ruling 8).
 * That default is wrong for a caller that decides something irreversible from the role — the
 * onboarding wizard picks its very first step from it at mount — so this hook also exposes
 * `isResolved`, for a caller that must wait for the real answer instead of the safe default.
 */
export function useMyTeamRoleStatus(options?: UseTeamListQueryOptions): MyTeamRoleStatus {
  // A skipped read (signed out, or `enabled: false`) is not loading: it resolves to the default.
  const { data, isLoading, error } = useTeamListQuery(options);
  if (isLoading) {
    return { role: 'OWNER', isResolved: false };
  }
  if (error || !data) {
    return { role: 'OWNER', isResolved: true };
  }
  return { role: data.myRole, isResolved: true };
}

/**
 * The caller's team role, for gating a checkout or upgrade action: only the workspace owner
 * manages billing (ruling 8). Cached for a few minutes so every screen that gates on it doesn't
 * refetch on its own. Loading or an error reads as `OWNER`, so an owner is never denied their
 * own button while `team.list` is still in flight.
 */
export function useMyTeamRole(): 'OWNER' | 'MEMBER' {
  return useMyTeamRoleStatus().role;
}
